import { AnimationDuration } from "@/constants/UI";
import useAssetsPicker from "@/hooks/useAssetsPicker";
import useAppMonitor from "@/services/AppMonitor";
import { Button, FAIconName } from "@/ui/core";
import type { ButtonVariant } from "@/ui/core/buttons/Button";
import { filePickerAssetFromImagePickerAsset } from "@/utils/filePicker.util";
import { isWeb } from "@/utils/platform.util";
import { joinTestIDs } from "@/utils/string.util";

interface PickFromCameraButtonProps extends ViewWithTestID {
  onAssetPicked: (asset: FilePickerAsset) => Promise<void> | void;
  onPress?: () => void;
  title: string;
  variant?: ButtonVariant;
}

const PickFromCameraButton = ({
  onAssetPicked,
  onPress,
  testID,
  title,
  variant,
}: PickFromCameraButtonProps) => {
  const { takePhoto } = useAssetsPicker();
  const { captureException } = useAppMonitor();

  if (isWeb()) {
    return null;
  }

  const handlePickImageFromCamera = async () => {
    try {
      const cameraAsset = await takePhoto({ onError: captureException });

      if (!cameraAsset) {
        return;
      }

      const pickerAsset = filePickerAssetFromImagePickerAsset(cameraAsset);
      await onAssetPicked(pickerAsset);
    } catch (error) {
      captureException(error);
    }
  };

  const handlePress = () => {
    onPress?.();
    // Wait for the bottom-sheet dismiss animation to finish before launching the
    // native camera. Launching immediately races the dismiss, so the sheet can
    // re-present on top of the camera (overlapping modal presentations).
    setTimeout(() => {
      void handlePickImageFromCamera();
    }, AnimationDuration.ModalDismiss);
  };

  return (
    <Button
      isFullWidth
      leftIcon={FAIconName.Camera}
      onPress={handlePress}
      testID={joinTestIDs(testID, "pick-from-camera")}
      title={title}
      variant={variant}
    />
  );
};

export default PickFromCameraButton;
